import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { hasSessionToken } from '../../utils/sessionCookie';
import { ellipsisApi as api, UserWithRelations } from '../ellipsisApi';
import type { RootState } from '../store';

interface initialStateInterface {
  loginModalOpen: boolean;
  authState: 'login' | 'create' | 'reset';
  authMessage: string;
  initialEmail: string;
  user: UserWithRelations | null;
  sessionRestoring: boolean;
  showCompleteProfileBanner: boolean;
  showVerifyEmailBanner: boolean;
}

export const hasSessionCookie = (): boolean =>
  typeof document !== 'undefined' && hasSessionToken(document.cookie);

// A lazy initialiser, so the cookie is read when the store is built rather than
// when this module is first imported. The two differ under SSR, where the module
// is evaluated once per server process and shared by every request.
const initialState = (): initialStateInterface => ({
  loginModalOpen: false,
  authState: 'login',
  authMessage: '',
  initialEmail: '',
  user: null,
  sessionRestoring: hasSessionCookie(),
  showCompleteProfileBanner: true,
  showVerifyEmailBanner: false,
});

const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    openLoginModal(state) {
      state.loginModalOpen = true;
    },
    closeLoginModal(state) {
      state.loginModalOpen = false;
      state.authMessage = '';
    },
    setAuthState(state, action) {
      state.authState = action.payload;
    },
    setAuthMessage(state, action) {
      state.authMessage = action.payload;
    },
    setInitialEmail(state, action) {
      state.initialEmail = action.payload;
    },
    hideCompleteProfileBanner(state) {
      state.showCompleteProfileBanner = false;
    },
    hideVerifyEmailBanner(state) {
      state.showVerifyEmailBanner = false;
      sessionStorage.setItem('verifyEmailBannerClosed', 'true');
    },
    checkVerifyEmailBanner(state) {
      state.showVerifyEmailBanner =
        sessionStorage.getItem('verifyEmailBannerClosed') !== 'true';
    },
    setUser(state, action) {
      state.user = action.payload;
    },
    startSessionRestore(state) {
      state.sessionRestoring = true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      api.endpoints.postUsersLogin.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
      }
    );
    builder.addMatcher(
      api.endpoints.postUsers.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
      }
    );
    builder.addMatcher(
      api.endpoints.getUsersMe.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
        state.sessionRestoring = false;
      }
    );
    // A rejected restore means the cookie is stale: stop treating its presence
    // as a session still on its way in.
    builder.addMatcher(api.endpoints.getUsersMe.matchRejected, (state) => {
      state.sessionRestoring = false;
    });
    builder.addMatcher(
      api.endpoints.postUsersRequestOwnerAuthorisation.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
      }
    );
    builder.addMatcher(
      api.endpoints.postUsersRequestOwnerAuthorisation.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
      }
    );
    builder.addMatcher(
      api.endpoints.postUsersUpdateProfilePicture.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
      }
    );
    builder.addMatcher(
      api.endpoints.postUsersUpdateUserInfo.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
      }
    );
    builder.addMatcher(
      api.endpoints.postUsersLogout.matchFulfilled,
      (state) => {
        document.cookie =
          'jwtToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        state.user = null;
        state.sessionRestoring = false;
      }
    );
  },
});

export const logoutUser = createAsyncThunk(
  'users/logout',
  async (payload: void, { dispatch }) => {
    try {
      const result = dispatch(api.endpoints.postUsersLogout.initiate());
      if (result && typeof (result as any).unsubscribe === 'function') {
        (result as any).unsubscribe();
      }
      await result;
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }
);

export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectSessionRestoring = (state: RootState) =>
  state.auth.sessionRestoring;

// The session token App reads to restore a signed-in user. Checked directly
// rather than through state so a page can tell, on its very first render,
// that a restore is coming — a flag set in an effect is always too late,
// because React runs child effects before the parent's.

export default authSlice;
